import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import { NEWS_ARTICLES, newsPath } from "@/content/news";
import { PAYMENT_METHODS } from "@/lib/data";
import { isLivePath } from "@/lib/publication";

type LinkTarget = {
  /** Doelpad (moet live staan). */
  path: string;
  /** Zoektermen, van lang naar kort; eerste treffer wint. */
  phrases: string[];
  /** Titel-attribuut voor extra context. */
  title: string;
};

/** Alle interne linkdoelen die organisch in lopende tekst mogen verschijnen. */
export const LINK_TARGETS: LinkTarget[] = [
  {
    path: "/review/axbet-uitbetaling",
    phrases: ["AX.bet uitbetaling", "AXbet uitbetaling", "uitbetaling bij AX.bet", "AX.bet opname"],
    title: "AX.bet uitbetaling: tijd, weeklimiet en review",
  },
  ...PAYMENT_METHODS.map((m) => ({
    path: `/betaalmethoden/${m.slug}`,
    phrases: methodPhrases(m.name),
    title: `${m.name} casino's vergelijken`,
  })),
  ...NEWS_ARTICLES.filter((a) => a.linkPhrases?.length).map((a) => ({
    path: newsPath(a),
    phrases: [...a.linkPhrases!].sort((x, y) => y.length - x.length),
    title: a.title,
  })),
  {
    path: "/casinozoeker",
    phrases: ["casinozoeker", "casino betaalzoeker", "betaalzoeker"],
    title: "Zoek casino's op betaalmethode",
  },
  {
    path: "/betaalmethoden",
    phrases: ["alle betaalmethoden", "overzicht van betaalmethoden"],
    title: "Overzicht van alle betaalmethoden",
  },
  {
    path: "/online-casino-snelle-uitbetaling",
    phrases: [
      "snelst uitbetalende casino's",
      "snel uitbetalende casino's",
      "casino's met een snelle uitbetaling",
      "snelle uitbetaling",
    ],
    title: "Online casino's met een snelle uitbetaling",
  },
  {
    path: "/online-casino-zonder-bkr",
    phrases: ["online casino zonder BKR", "casino zonder BKR", "casino's zonder BKR", "zonder BKR-toets", "zonder BKR"],
    title: "Online casino zonder BKR: spelen zonder kredietcheck",
  },
  {
    path: "/casino-zonder-cruks",
    phrases: ["casino zonder Cruks", "casino's zonder Cruks", "online casino zonder Cruks"],
    title: "Casino zonder Cruks: risico's en legale alternatieven",
  },
  {
    path: "/online-casino-zonder-idin",
    phrases: ["online casino zonder iDIN", "casino zonder iDIN", "casino's zonder iDIN", "zonder iDIN verifiëren", "zonder iDIN"],
    title: "Online casino zonder iDIN: alternatieve verificatie getest",
  },
  {
    path: "/review/tonybet-uitbetaling",
    phrases: ["TonyBet uitbetaling", "uitbetaling bij TonyBet", "uitbetaling TonyBet"],
    title: "TonyBet uitbetaling: tijd, methoden en limieten",
  },
  {
    path: "/review/one-casino-uitbetaling",
    phrases: ["One Casino uitbetaling", "uitbetaling bij One Casino", "OneCasino uitbetaling"],
    title: "One Casino uitbetaling: tijd, status en limieten",
  },
  {
    path: "/review/bet365-uitbetaling",
    phrases: ["bet365 uitbetaling", "uitbetaling bij bet365", "uitbetaling bet365"],
    title: "bet365 uitbetaling: tijd, methoden en limieten",
  },
  {
    path: "/review/toto-uitbetaling",
    phrases: ["TOTO uitbetaling", "uitbetaling bij TOTO", "uitbetaling TOTO"],
    title: "TOTO uitbetaling: tijd, limieten en verificatie",
  },
  {
    path: "/review/jacks-uitbetaling",
    phrases: ["Jack's uitbetaling", "uitbetaling bij Jack's", "JACKS.NL uitbetaling"],
    title: "Jack's uitbetaling: tijd, limieten en verificatie",
  },
  {
    path: "/review/wakuna-uitbetaling",
    phrases: ["Wakuna uitbetaling", "uitbetaling bij Wakuna", "uitbetaling Wakuna"],
    title: "Wakuna uitbetaling: tijd, limieten en verificatie",
  },
  {
    path: "/review/spinboss-uitbetaling",
    phrases: ["Spinboss uitbetaling", "uitbetaling bij Spinboss", "uitbetaling Spinboss"],
    title: "Spinboss uitbetaling: tijd, limieten en verificatie",
  },
  {
    path: "/review/kokobet-uitbetaling",
    phrases: ["Kokobet uitbetaling", "uitbetaling bij Kokobet", "uitbetaling Kokobet"],
    title: "Kokobet uitbetaling: tijd, methoden en limieten",
  },
  {
    path: "/online-casino-buitenland",
    phrases: ["online casino buitenland", "casino online buitenland", "online casino in het buitenland", "buitenlandse goksites", "casino in het buitenland"],
    title: "Online casino buitenland: legaal, betrouwbaar en wat het kost",
  },
  {
    path: "/nieuwe-online-casinos",
    phrases: ["nieuwe online casino's in Nederland", "nieuwe online casino's", "nieuwe casino's", "nieuw online casino", "nieuw casino"],
    title: "Nieuwe online casino's in Nederland getest",
  },
  {
    path: "/betrouwbare-online-casinos",
    phrases: ["betrouwbare online casino's", "betrouwbaar online casino", "betrouwbare casino's", "betrouwbaar casino", "veilig online casino"],
    title: "Betrouwbare online casino's in Nederland",
  },
  {
    path: "/idin-casinos",
    phrases: ["iDIN-verificatie", "iDIN casino's", "verifiëren met iDIN", "iDIN"],
    title: "iDIN casino's: identiteitsverificatie via je bank",
  },
  {
    path: "/best-uitbetalende-online-casinos",
    phrases: [
      "best uitbetalende online casino's",
      "best uitbetalende casino's",
      "hoogste uitbetalingspercentage",
      "uitbetalingspercentage",
    ],
    title: "Best uitbetalende online casino's op RTP",
  },
  {
    path: "/online-casino-zonder-limiet",
    phrases: ["online casino zonder limiet", "casino zonder limiet", "hoogste stortlimiet", "stort- en opnamelimieten", "opnamelimieten"],
    title: "Online casino zonder limiet: limieten getest",
  },
  {
    path: "/lage-storting/1-euro-deposit-casinos",
    phrases: ["1 euro deposit casino", "1 euro storten", "storting van één euro", "vanaf één euro storten"],
    title: "Casino's met een storting van 1 euro",
  },
  {
    path: "/lage-storting/5-euro-deposit-casinos",
    phrases: ["casino 5 euro storten", "5 euro storten", "storting van 5 euro"],
    title: "Casino's met een storting van 5 euro",
  },
  {
    path: "/lage-storting/10-euro-deposit-casinos",
    phrases: ["casino 10 euro storten", "10 euro storten", "storting van tien euro", "tien euro storten"],
    title: "Casino's met een storting van 10 euro",
  },
  {
    path: "/lage-storting/20-euro-deposit-casinos",
    phrases: ["casino 20 euro storten", "20 euro storten", "storting van 20 euro", "twintig euro storten"],
    title: "Casino's met een storting van 20 euro",
  },
  {
    path: "/methodologie",
    phrases: ["onze methodologie", "onze testmethode", "hoe wij beoordelen"],
    title: "Zo beoordelen wij casino's en betaalmethoden",
  },
  {
    path: "/verantwoord-spelen",
    phrases: ["verantwoord spelen", "verantwoord speelgedrag", "speellimieten"],
    title: "Verantwoord spelen: limieten, signalen en hulp",
  },
  {
    path: "/nieuws",
    phrases: ["betaalnieuws", "ons nieuws"],
    title: "Nieuws over betalen bij casino's",
  },
  {
    path: "/auteurs",
    phrases: ["onze redactie", "de redactie"],
    title: "Over onze redactie",
  },
].filter((t) => isLivePath(t.path));


/** Langere natuurlijke varianten eerst, zodat anchors niet altijd alleen de merknaam zijn. */
function methodPhrases(name: string): string[] {
  const names = name === "iDEAL" ? [name, "iDeal"] : [name];
  const out: string[] = [];
  for (const n of names) {
    out.push(`casino met ${n}`, `${n} casino's`, `${n} casino`, `met ${n} storten`, `${n} storten`, `met ${n} betalen`);
  }
  return [...out, ...names];
}

/**
 * Pagina's met relatief weinig inkomende links. Krijgen voorrang op de
 * overige doelen wanneer het linkbudget van een pagina krap is.
 */
export const WEAK_LINK_PATHS = [
  "/review/axbet-uitbetaling",
  "/nieuwe-online-casinos",
  "/betrouwbare-online-casinos",
  "/online-casino-zonder-bkr",
  "/review/tonybet-uitbetaling",
"/review/one-casino-uitbetaling",
  "/review/bet365-uitbetaling",
  "/review/kokobet-uitbetaling",
  "/review/toto-uitbetaling",
  "/review/jacks-uitbetaling",
  "/review/wakuna-uitbetaling",
  "/review/spinboss-uitbetaling",
  "/online-casino-buitenland",
  "/online-casino-zonder-limiet",
  "/lage-storting/1-euro-deposit-casinos",
  "/online-casino-zonder-idin",
  "/idin-casinos",
  "/betaalmethoden/afterpay-casinos",
  "/betaalmethoden/klarna",
  "/betaalmethoden/neosurf",
  "/betaalmethoden/muchbetter",
  "/betaalmethoden/mastercard",
  "/betaalmethoden/visa",
  "/betaalmethoden/sofort",
  "/betaalmethoden/hipay",
  "/betaalmethoden/revolut",
];

/**
 * Houdt bij hoeveel interne links er al in één pagina zijn geplaatst.
 * Zo blijft de linking organisch: één link per doel, één per alinea en
 * een harde bovengrens per pagina.
 */
export class LinkBudget {
  private counts = new Map<string, number>();
  private placed = 0;
  private blocksSinceLink = Number.POSITIVE_INFINITY;
  private priority = new Set<string>();

  constructor(
    /** Pad van de huidige pagina; daar linken we nooit naartoe. */
    private readonly currentPath: string,
    private readonly maxPerPage = 12,
    /** Minimaal aantal tekstblokken tussen twee links — houdt de links verspreid. */
    private readonly minGap = 4,
    /** Maximaal aantal links naar hetzelfde doel per pagina. */
    private readonly maxPerTarget = 1,
    /** Doelen die voorrang krijgen (bv. de money pages van dit artikel). */
    priorityPaths: readonly string[] = [],
  ) {
    this.counts.set(normalize(currentPath), Number.POSITIVE_INFINITY);
    for (const p of priorityPaths) this.priority.add(normalize(p));
  }

  isPriority(path: string) {
    return this.priority.has(normalize(path));
  }

  canUse(path: string) {
    return (
      this.placed < this.maxPerPage &&
      this.blocksSinceLink >= this.minGap &&
      (this.counts.get(normalize(path)) ?? 0) < this.maxPerTarget
    );
  }

  take(path: string) {
    const key = normalize(path);
    this.counts.set(key, (this.counts.get(key) ?? 0) + 1);
    this.placed += 1;
    this.blocksSinceLink = 0;
  }

  /** Wordt aangeroepen voor elk tekstblok dat géén link kreeg. */
  skip() {
    this.blocksSinceLink += 1;
  }
}

function normalize(path: string) {
  return (path.split(/[?#]/)[0] ?? "").replace(/\/+$/, "") || "/";
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Vervangt de eerste geschikte vermelding van een linkdoel in een tekst door
 * een echte interne link. Er wordt maximaal één link per tekstblok geplaatst,
 * zodat de alinea leesbaar blijft.
 */
export function linkifyText(
  text: string,
  budget: LinkBudget,
  keyPrefix: string,
  /** Doel dat in dit blok verplicht gelinkt moet worden (negeert gap/limiet). */
  forcePath?: string,
): ReactNode {
  let best: { index: number; length: number; target: LinkTarget } | null = null;

  for (const target of LINK_TARGETS) {
    const forced = forcePath ? normalize(forcePath) === normalize(target.path) : false;
    if (!forced && !budget.canUse(target.path)) continue;
    for (const phrase of target.phrases) {
      const re = new RegExp(`(^|[^\\p{L}\\p{N}-])(${escapeRegExp(phrase)})(?=$|[^\\p{L}\\p{N}-])`, "iu");
      const match = re.exec(text);
      if (!match || match.index === undefined) continue;
      const index = match.index + (match[1]?.length ?? 0);
      const isPriority = forced || budget.isPriority(target.path);
      const better =
        !best ||
        (isPriority && !budget.isPriority(best.target.path)) ||
        (isPriority === budget.isPriority(best.target.path) && index < best.index);
      if (better) {
        best = { index, length: match[2]!.length, target };
      }
      break;
    }
    if (forced && best && normalize(best.target.path) === normalize(forcePath!)) break;
  }


  if (!best) {
    budget.skip();
    return text;
  }

  budget.take(best.target.path);
  const before = text.slice(0, best.index);
  const label = text.slice(best.index, best.index + best.length);
  const after = text.slice(best.index + best.length);

  return (
    <>
      {before}
      <Link
        key={`${keyPrefix}-link`}
        to={best.target.path as "/"}
        title={best.target.title}
        className="font-medium text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:decoration-primary"
      >
        {label}
      </Link>
      {after}
    </>
  );
}

/** Bevat deze tekst een term die naar dit doel kan linken? */
export function textMentions(path: string, text: string): boolean {
  const target = LINK_TARGETS.find((t) => normalize(t.path) === normalize(path));
  if (!target) return false;
  return target.phrases.some((phrase) =>
    new RegExp(`(^|[^\\p{L}\\p{N}-])(${escapeRegExp(phrase)})(?=$|[^\\p{L}\\p{N}-])`, "iu").test(text),
  );
}

/** Money pages die elk artikel minimaal één keer moet linken als ze genoemd worden. */
export const REQUIRED_LINK_PATHS = ["/betaalmethoden/ideal"];
